import { Injectable, inject } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { map, shareReplay, startWith, scan, catchError, timeout } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import { FilterStore } from './filter.store';
import {
  FilterId,
  FilterSchema,
  FilterSelection,
  ColumnFiltersApiShape,
  FilterSchemaItem,
} from './filter.types';
import { buildFilterSchemaFromUiConfig } from './schema.adapter';
import { UIConfig } from '../../shared/models/ui-config.model';
import { FilterConfig } from '../../shared/models/filter-config.model';
import { PageKey } from '../../shared/models/page-key.enum';
import { UiConfigService } from '../../shared/services/config/ui-config.service';
import { DashboardApiService } from '../../shared/services/api/dashboard-api.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { NULL_TOKEN } from '../../shared/models/filter.model';
import { TenantContextService } from '../tenant/tenant-context.service';
import {
  CompareMode,
  DateRangeDefaultsService,
  DateRangeISO,
  DateRangeLocal,
} from './date-range-defaults.service';
import { QuickDatePreset } from '../../shared/services/date-presets/date-range.service';

@Injectable({ providedIn: 'root' })
export class FilterFacade {
  private static readonly PREFETCH_REQUEST_TIMEOUT_MS = 8_000;

  private store = inject(FilterStore);
  private uiConfigService = inject(UiConfigService);
  private dashboardApi = inject(DashboardApiService);
  private tenantContext = inject(TenantContextService);
  private dateRangeDefaults = inject(DateRangeDefaultsService);
  private initializedSwitchVersion = -1;
  private lastPrefetchedSwitchVersion = -1;

  private applied$ = new Subject<void>();

  readonly activeCount = this.store.activeCount;
  readonly selections = this.store.selections;
  readonly state$ = toObservable(this.store.selections);

  /**
   * Prefetch all filter options for a tenant in parallel and cache them.
   * This should be called after tenant selection.
   */
  async prefetchAllFilterOptions(tenantKey: string): Promise<void> {
    const switchContext = this.tenantContext.getTenantSwitchContext();
    if (this.lastPrefetchedSwitchVersion === switchContext.version) return;

    try {
      this.lastPrefetchedSwitchVersion = switchContext.version;

      const config = await firstValueFrom(this.uiConfigService.loadTenantConfig(tenantKey));
      if (!config) {
        return;
      }
      const schema = buildFilterSchemaFromUiConfig(config);
      this.init(tenantKey, schema);

      const existingCache = this.store.restoreFilterOptions(tenantKey);
      if (schema) {
        const needFullFetch = !existingCache;
        if (needFullFetch) {
          await this.fetchFilterOptionsInParallel(tenantKey, schema);
        } else {
          const apiFilters = Object.entries(schema.filters).filter(
            ([, filter]) =>
              (filter as FilterSchemaItem).source === 'api' && (filter as FilterSchemaItem).column
          );
          const toFetch = apiFilters
            .map(([fid]) => fid)
            .filter((fid) => {
              const val = (existingCache as Record<string, unknown>)[fid];
              return !Array.isArray(val) || (Array.isArray(val) && val.length === 0);
            });
          if (toFetch.length > 0) {
            await this.fetchFilterOptionsInParallel(tenantKey, schema, toFetch);
          }
        }
      }
    } catch (error) {
      console.error('FilterFacade: Error in prefetchAllFilterOptions:', error);
      this.lastPrefetchedSwitchVersion = -1;
    }
  }

  private async fetchFilterOptionsInParallel(
    tenantKey: string,
    schema: FilterSchema,
    filterIds?: string[]
  ): Promise<void> {
    const apiFilters = Object.entries(schema.filters).filter(
      ([fid, filter]) =>
        (!filterIds || filterIds.includes(fid)) &&
        (filter as FilterSchemaItem).source === 'api' &&
        (filter as FilterSchemaItem).column
    );
    if (apiFilters.length === 0) return;

    const fetchRequests = apiFilters.map(([filterId, filter]) => {
      const apiFilter = filter as FilterSchemaItem;
      return this.dashboardApi
        .getColumnValues(apiFilter.column, {
          fetch_distinct: apiFilter.fetchDistinct ?? true,
          order_direction: apiFilter.orderDirection ?? 'asc',
          page: 0,
          size: 100,
        })
        .pipe(
          timeout(FilterFacade.PREFETCH_REQUEST_TIMEOUT_MS),
          map((response) => ({ filterId, options: this.mapResponseToOptions(response) })),
          catchError((error) => {
            console.error('FilterFacade: Error fetching options for filter:', filterId, error);
            return of({ filterId, options: [] });
          })
        );
    });

    const ids = apiFilters.map(([fid]) => fid);
    this.store.setBulkFilterOptionsLoading(ids, true);
    try {
      const results = await firstValueFrom(forkJoin(fetchRequests));
      const existingCache =
        (this.store.restoreFilterOptions(tenantKey) as Record<string, unknown>) || {};
      const merged: Record<string, string[]> = { ...existingCache } as Record<string, string[]>;
      for (const { filterId, options } of results as { filterId: string; options: string[] }[]) {
        const prev = existingCache[filterId] as string[] | undefined;
        if (options.length > 0 || !Array.isArray(prev) || prev.length === 0) {
          merged[filterId] = options;
        }
      }
      this.store.cacheFilterOptions(tenantKey, merged);
    } catch (error) {
      console.error('FilterFacade: Error in fetchFilterOptionsInParallel:', error);
    } finally {
      this.store.setBulkFilterOptionsLoading(ids, false);
    }
  }

  private mapResponseToOptions(response: { data?: unknown[] } | undefined): string[] {
    return (response?.data || []).map((row) => {
      const raw = Object.values(row as Record<string, unknown>)[0];
      if (raw === null || raw === undefined) return NULL_TOKEN;
      const s = String(raw);
      return s.trim() === '' ? NULL_TOKEN : s;
    });
  }

  readonly version$ = this.applied$.pipe(
    startWith(void 0),
    scan((v) => v + 1, 0),
    shareReplay(1)
  );

  readonly filterChanges$ = this.version$.pipe(
    map(() => ({
      tenantKey: this.tenantContext.currentTenantKey(),
      selections: this.store.getSelectionsSnapshot(),
      getScopedColumnFilters: (page: PageKey): ColumnFiltersApiShape =>
        this.store.scopedColumnFilters(page),
    })),
    shareReplay(1)
  );

  init(tenantKey: string, schema: FilterSchema): void {
    const currentVersion = this.tenantContext.getTenantSwitchContext().version;
    if (this.initializedSwitchVersion === currentVersion) return;
    this.store.loadSchema(tenantKey, schema);
    this.initializedSwitchVersion = currentVersion;
  }

  initWithUiConfig(tenantKey: string, uiConfig: UIConfig): void {
    const currentVersion = this.tenantContext.getTenantSwitchContext().version;
    if (this.initializedSwitchVersion === currentVersion) return;
    const schema = buildFilterSchemaFromUiConfig(uiConfig);
    this.store.loadSchema(tenantKey, schema);
    this.initializedSwitchVersion = currentVersion;
  }

  tenantKey(): string {
    return this.tenantContext.currentTenantKey();
  }

  setSelection(filterId: FilterId, selection: FilterSelection): void {
    this.store.setSelection(filterId, selection);
  }

  setOperator(filterId: FilterId, op: 'in' | 'not_in'): void {
    this.store.setOperator(filterId, op);
  }

  /**
   * Raw date-range writer. Prefer {@link setDateRangeFromPreset} or
   * {@link setDateRangeFromUserPick}; this stays for sanitization paths.
   * Intent fields merge: omitted keys preserve the prior value; explicit
   * ``undefined`` clears.
   */
  setDateRange(value: {
    start: string;
    end: string;
    compareStart?: string;
    compareEnd?: string;
    compareIntent?: 'preset' | 'user';
    compareMode?: CompareMode;
    sourcePreset?: QuickDatePreset;
  }) {
    const existing = this.getDateRangeWithMeta();
    const merged = {
      start: value.start,
      end: value.end,
      compareStart: value.compareStart,
      compareEnd: value.compareEnd,
      compareIntent: 'compareIntent' in value ? value.compareIntent : existing.compareIntent,
      compareMode: 'compareMode' in value ? value.compareMode : existing.compareMode,
      sourcePreset: 'sourcePreset' in value ? value.sourcePreset : existing.sourcePreset,
    };
    this.store.setSelection('date_range', { type: 'dateRange', value: merged });
  }

  /** Persist a preset-derived date range tagged with the source preset + mode. */
  setDateRangeFromPreset(
    value: { start: string; end: string; compareStart: string; compareEnd: string },
    opts: { preset: QuickDatePreset; compareMode: CompareMode }
  ): void {
    this.setDateRange({
      ...value,
      compareIntent: 'preset',
      compareMode: opts.compareMode,
      sourcePreset: opts.preset,
    });
  }

  /** Persist a user-picked date range. Honored verbatim across page navigation. */
  setDateRangeFromUserPick(value: {
    start: string;
    end: string;
    compareStart?: string;
    compareEnd?: string;
  }): void {
    this.setDateRange({
      ...value,
      compareIntent: 'user',
      compareMode: undefined,
      sourcePreset: undefined,
    });
  }

  reset(): void {
    this.store.reset(this.tenantContext.currentTenantKey());
  }

  apply(): void {
    this.store.apply(this.tenantContext.currentTenantKey());
    this.applied$.next();
  }

  scopedColumnFilters(page: PageKey): ColumnFiltersApiShape {
    return this.store.scopedColumnFilters(page);
  }

  getDateRangeISO(): DateRangeISO {
    const meta = this.getDateRangeWithMeta();
    return {
      start: meta.start,
      end: meta.end,
      compareStart: meta.compareStart,
      compareEnd: meta.compareEnd,
    };
  }

  /** Persisted date range plus its compare-intent metadata. */
  getDateRangeWithMeta(): DateRangeISO & {
    compareIntent?: 'preset' | 'user';
    compareMode?: CompareMode;
    sourcePreset?: QuickDatePreset;
  } {
    const sel = (this.store.selections() as Record<string, { type: string; value?: unknown }>)[
      'date_range'
    ] as
      | {
          type: string;
          value?: {
            start?: string;
            end?: string;
            compareStart?: string;
            compareEnd?: string;
            compareIntent?: 'preset' | 'user';
            compareMode?: CompareMode;
            sourcePreset?: QuickDatePreset;
          };
        }
      | undefined;
    const value = sel && sel.type === 'dateRange' ? sel.value || {} : {};
    return {
      start: value.start,
      end: value.end,
      compareStart: value.compareStart,
      compareEnd: value.compareEnd,
      compareIntent: value.compareIntent,
      compareMode: value.compareMode,
      sourcePreset: value.sourcePreset,
    };
  }

  getDateRangeLocal(): DateRangeLocal {
    const iso = this.getDateRangeISO();
    return {
      start: this.dateRangeDefaults.parseLocalDate(iso.start),
      end: this.dateRangeDefaults.parseLocalDate(iso.end),
      compareStart: this.dateRangeDefaults.parseLocalDate(iso.compareStart),
      compareEnd: this.dateRangeDefaults.parseLocalDate(iso.compareEnd),
    };
  }

  /**
   * Reconcile the persisted date range against the entering page's
   * ``compareMode``. Branches: missing dates → seed; intent='user' →
   * honor; intent='preset' with matching mode → no-op; otherwise keep
   * main and recompute compare via the canonical preset helper.
   * ``force=true`` always re-seeds (tenant-switch reset path).
   * Returns ``true`` when state changed.
   */
  ensureDateRangeDefaults(force = false, compareMode: CompareMode = 'prior-period'): boolean {
    if (force) {
      return this.seedDateRangeDefault(compareMode);
    }

    const meta = this.getDateRangeWithMeta();
    const haveCompleteRange = meta.start && meta.end && meta.compareStart && meta.compareEnd;
    if (!haveCompleteRange) {
      return this.seedDateRangeDefault(compareMode);
    }
    if (meta.compareIntent === 'user') {
      return false;
    }
    if (meta.compareIntent === 'preset' && meta.compareMode === compareMode) {
      return false;
    }

    const mainStart = this.dateRangeDefaults.parseLocalDate(meta.start);
    const mainEnd = this.dateRangeDefaults.parseLocalDate(meta.end);
    if (!mainStart || !mainEnd) {
      return this.seedDateRangeDefault(compareMode);
    }
    const preset: QuickDatePreset = meta.sourcePreset ?? 'lastQuarter';
    const { compareStart, compareEnd } = this.dateRangeDefaults.calculateCompareForPreset(
      preset,
      mainStart,
      mainEnd,
      compareMode
    );
    this.setDateRangeFromPreset(
      {
        start: meta.start as string,
        end: meta.end as string,
        compareStart: this.dateRangeDefaults.formatLocalDate(compareStart),
        compareEnd: this.dateRangeDefaults.formatLocalDate(compareEnd),
      },
      { preset, compareMode }
    );
    this.apply();
    return true;
  }

  private seedDateRangeDefault(compareMode: CompareMode): boolean {
    const { start, end, compareStart, compareEnd } =
      this.dateRangeDefaults.calculateDefaultDateRanges(compareMode);
    this.setDateRangeFromPreset(
      {
        start: this.dateRangeDefaults.formatLocalDate(start),
        end: this.dateRangeDefaults.formatLocalDate(end),
        compareStart: this.dateRangeDefaults.formatLocalDate(compareStart),
        compareEnd: this.dateRangeDefaults.formatLocalDate(compareEnd),
      },
      { preset: 'lastQuarter', compareMode }
    );
    this.apply();
    return true;
  }

  buildFilterConfigFromDates(compareMode: CompareMode = 'prior-period'): FilterConfig {
    return this.dateRangeDefaults.buildFilterConfigFromDates(this.getDateRangeLocal(), compareMode);
  }

  buildDefaultFilterConfig(compareMode: CompareMode = 'prior-period'): FilterConfig {
    return this.dateRangeDefaults.buildDefaultFilterConfig(compareMode);
  }

  getEffectiveMaxDate(): Date {
    return this.dateRangeDefaults.getEffectiveMaxDate();
  }
}
